import React, {useEffect} from 'react'
import {usePage} from '@salesforce/commerce-sdk-react'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
// Components
import {StackProps, TextProps, VStack, useMultiStyleConfig} from '@chakra-ui/react'
import {Widget} from '_uiComponents'
//Hooks
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useAnalytics, useCurrentBasket, usePageDesigner, useHeaderSize} from '_customHooks'
import {useGlobalConfigState /*, useHeaderDispatch*/} from '_context'
import {MetaTags, useSeoUrls, setDyRecommendationContext, setContentPageCacheHeaders, resolveHostLink} from '_utils'
import {useIntl} from 'react-intl'

const Home = () => {
    let isFirstLoad = false
    const PAGE_DESIGNER_PAGE_ID = 'homepage'

    const {res} = useServerContext()
    const {locale, site} = useMultiSite()

    // SEO URLs — canonical + hreflang. useSeoUrls resolves the public host from the hostLink
    // preference via a dedicated react-query, which withReactQuery awaits during SSR so the
    // tags are present in the initial HTML with the correct domain (not the internal MRT host).
    const seoUrls = useSeoUrls()

    if (res) {
        setContentPageCacheHeaders(res)
        // Only set the canonical Link header when the public host (from hostLink) is resolved.
        // No fallback to getAppOrigin() — in SSR that returns the internal MRT origin
        // (pwa-*.mobify-storefront.com), which must never appear in canonical/alternate URLs.
        if (seoUrls?.canonical) {
            res.set('Link', `<${seoUrls.canonical}>; rel="canonical"`)
        }
    }

    const {analyticsEventInit, analyticsEventPwaPage} = useAnalytics()
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {resetPageDesignerPosition, renderPageDesignerRegion} = usePageDesigner()
    const {headerSize, accessibilityBannerSize} = useHeaderSize(false)
    const einstein = useEinstein()
    const {appPreferences} = useGlobalConfigState()
    // const {setTransparentMode} = useHeaderDispatch()
    const {disableEditorialWidget} = appPreferences
    const {formatMessage} = useIntl()

    // Public host for absolute structured-data URLs (SSR-safe). See development.md §9.7.
    const publicHost = resolveHostLink(appPreferences?.hostLink, site?.id)

    const styles = useMultiStyleConfig('HomePage', {headerSize, accessibilityBannerSize}) as Record<string, StackProps & TextProps>

    const {data: homepagePD} = usePage({parameters: {pageId: PAGE_DESIGNER_PAGE_ID}})
    resetPageDesignerPosition()

    const metaProps = {
        title: formatMessage({id: 'seo.home.title', defaultMessage: 'Brunello Cucinelli: Official boutique for women, men and kids'}),
        description: formatMessage({
            id: 'seo.home.description',
            defaultMessage:
                'Discover Brunello Cucinelli collections on the official online boutique. Find out latest arrivals and get inspired by our looks for women, men and kids.',
        }),
        keywords: formatMessage({id: 'seo.home.keywords', defaultMessage: 'Brunello Cucinelli - official boutique'}),
        // Organization (logo) structured data must use ABSOLUTE public URLs available in SSR.
        // Source the public host from the hostLink preference (same as canonical/hreflang) rather
        // than window.location — the latter is undefined during SSR, so the initial HTML would ship
        // an empty url/logo. No fallback to getAppOrigin() (internal MRT host). See development.md §9.7.
        jsonLd: publicHost
            ? JSON.stringify({
                  '@context': 'http://schema.org/',
                  '@type': 'Organization',
                  url: seoUrls?.canonical ?? `https://${publicHost}/`,
                  logo: `https://${publicHost}/logo.svg`,
              })
            : undefined,
        alternateUrls: seoUrls?.alternate ?? [],
        canonicalUrl: seoUrls?.canonical ?? '',
    }

    /**
     * Triggers `analyticsEventPwaPage` when data layer is already initialized.
     * @remarks
     * The data layer check ensures that the event will be triggered
     * only between PWA-pages navigation.
     */
    const runPwaPageEvent = () => {
        if (window && window.dataLayer) {
            analyticsEventPwaPage({
                pageCategory: 'homepage',
                pagesubCategory: '',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: '',
            })
        }
    }

    useEffect(() => {
        if (customer && locale && basket && !isFirstLoad) {
            analyticsEventInit({
                country: locale.id.split('-')[1],
                currency: basket.currency,
                pageCategory: 'homepage',
                pageSubCategory: 'neutral',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: '200',
                listingType: '',
            })
            isFirstLoad = true
        }
    }, [customer, locale, basket])

    useEffect(() => {
        einstein.sendViewPage(window.location.href)
        runPwaPageEvent()
        setDyRecommendationContext({type: 'HOMEPAGE', localeId: locale?.id})
    }, [])

    // useEffect(() => {
    //     if (homepagePD?.typeId === 'fullPage') {
    //         setTransparentMode(false)
    //     }
    // }, [homepagePD])

    return (
        <VStack {...styles.container} data-testid="homepage">
            <MetaTags {...metaProps} />

            {homepagePD && homepagePD.regions?.map((region, idx) => renderPageDesignerRegion({region, isMainBanner: idx === 0, analyticsName: 'homepage'}))}

            {(disableEditorialWidget === null ||
                (typeof disableEditorialWidget !== 'undefined' && disableEditorialWidget !== null && disableEditorialWidget.indexOf('HOMEPAGE') === -1)) && (
                <Widget />
            )}
        </VStack>
    )
}

Home.getTemplateName = () => 'home'

export default Home
